import React, { useState, ReactNode } from 'react';
import { Button } from './ui/button';
import oneSpaceApi from '../lib/onespace-api';
import { GripVertical, Settings } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

interface WidgetWrapperProps {
  children: ReactNode;
  title: string;
  widgetId: string;
  widgetType: string;
  isTeamWidget?: boolean;
  permission?: 'view' | 'edit';
  isPinned?: boolean;
  onSettingsUpdate?: (settings: any) => void;
  settings: any;
}

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  children,
  title,
  widgetId,
  widgetType,
  isTeamWidget = false,
  permission = 'view',
  isPinned = false,
  onSettingsUpdate,
  settings
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isPinnedState, setIsPinnedState] = useState(isPinned);
  const [widgetSettings, setWidgetSettings] = useState(settings);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleMouseEnter = () => {
    if (permission === 'edit') {
      setShowMenu(true);
    }
  };

  const handleMouseLeave = () => {
    setShowMenu(false);
  };

  const togglePin = async () => {
    setIsUpdating(true);
    try {
      const newPinnedState = !isPinnedState;
      setIsPinnedState(newPinnedState);
      
      // In een echte applicatie zou je dit naar de API sturen
      // await oneSpaceApi.post(`/api/widgets/${widgetId}/pin`, {
      //   isPinned: newPinnedState
      // });
      
      // Sluit menu na actie
      setShowMenu(false);
    } catch (error) {
      console.error('Error updating pin status:', error);
      // Herstel bij fout
      setIsPinnedState(isPinnedState);
    } finally {
      setIsUpdating(false);
    }
  };

  const saveSettings = async () => {
    setIsUpdating(true);
    try {
      if (onSettingsUpdate) {
        onSettingsUpdate(widgetSettings);
      }
      console.log("API call to save settings would happen here with:", widgetSettings);
    } catch (error) {
      console.error('Error updating widget settings:', error);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const renderSettingsForm = () => {
    return (
      <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1 pr-3">
        {Object.entries(widgetSettings).map(([key, value]) => {
          if (['id', 'type', 'template', 'instanceId', 'position', 'size', 'isPinned', 'isTeamWidget'].includes(key)) return null;
          
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return (
              <div key={key} className="text-sm text-gray-500 dark:text-gray-400">
                {key}: Complexe waarde (object)
              </div>
            );
          } 
          
          if (Array.isArray(value)) {
            return (
               <div key={key} className="space-y-1">
                 <label className="block text-sm text-gray-600 dark:text-gray-300 font-medium">{key} (Array)</label>
                 <textarea
                    value={value.join(', \n')}
                    onChange={(e) => {
                       const updatedSettings = { ...widgetSettings, [key]: e.target.value.split(/,\s*\n?/).map(s => s.trim()).filter(Boolean) };
                       setWidgetSettings(updatedSettings);
                    }}
                    className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md min-h-[60px] text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                    rows={3}
                 />
               </div>
             );
          }
          
          return (
            <div key={key} className="space-y-1">
              <label className="block text-sm text-gray-600 dark:text-gray-300 font-medium capitalize">{key.replace(/_/g, ' ')}</label>
              {typeof value === 'boolean' ? (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={value as boolean}
                    onChange={(e) => {
                      const updatedSettings = { ...widgetSettings, [key]: e.target.checked };
                      setWidgetSettings(updatedSettings);
                    }}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary dark:bg-gray-700"
                  />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    {value ? 'Ingeschakeld' : 'Uitgeschakeld'}
                  </span>
                </div>
              ) : typeof value === 'number' ? (
                <input
                  type="number"
                  value={value as number}
                  onChange={(e) => {
                    const updatedSettings = { ...widgetSettings, [key]: Number(e.target.value) };
                    setWidgetSettings(updatedSettings);
                  }}
                  className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                />
              ) : typeof value === 'string' ? (
                <input
                  type="text"
                  value={value as string}
                  onChange={(e) => {
                    const updatedSettings = { ...widgetSettings, [key]: e.target.value };
                    setWidgetSettings(updatedSettings);
                  }}
                  className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                />
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Waarde type {typeof value}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div 
      className="relative h-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="h-full">
         {children}
      </div>
      
      {permission === 'edit' && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`absolute top-2 right-2 z-10 h-8 w-8 p-0 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary/10 dark:hover:bg-primary/20 hover:text-primary dark:hover:text-primary transition-all duration-150 ${showMenu ? 'opacity-100' : 'opacity-0'}`}
              title="Widget instellingen"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-[600px] max-w-[90vw] shadow-xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-semibold text-gray-800 dark:text-white flex items-center">
                <Settings className="h-5 w-5 text-primary mr-2" />
                Instellingen voor "{title}"
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-500 dark:text-gray-400">
                Pas de widget-instellingen aan naar wens
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            {renderSettingsForm()}

            <AlertDialogFooter className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <AlertDialogCancel 
                 className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Annuleren
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  await saveSettings();
                }}
                className="bg-primary hover:bg-primary/90 text-white font-medium transition-colors"
                disabled={isUpdating}
              >
                 {isUpdating ? 'Opslaan...' : 'Opslaan'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      
      {isPinnedState && (
        <div className="absolute bottom-2 left-2 z-10">
          <div className="h-5 w-5 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center shadow-sm" title="Vastgepind">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-primary" viewBox="0 0 20 20" fill="currentColor">
               <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L3.707 10.707a1 1 0 01-1.414-1.414l6-6z" clipRule="evenodd" /> 
             </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetWrapper; 